import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, MenuItem, Button,
  Chip, CircularProgress, InputAdornment, IconButton, Tooltip, Avatar,
  Collapse, Grid, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  Refresh, Search, ExpandMore, ExpandLess, Add, Download,
  CreditCard, CheckCircle, Cancel,
} from '@mui/icons-material';
import { InvokeApi, postImg, apiList } from '../../services/ApiService';
import { Config } from '../../config/Config';
import dayjs from 'dayjs';

export default function EscortCards() {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [standard, setStandard] = useState('');
  const [section, setSection] = useState('');
  const [search, setSearch] = useState('');
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [expanded, setExpanded] = useState(new Set());
  const [generating, setGenerating] = useState({});
  const [addDialog, setAddDialog] = useState(false);
  const [addStudent, setAddStudent] = useState(null);
  const [addStudentId, setAddStudentId] = useState('');
  const [addForm, setAddForm] = useState({
    escort_name: '', escort_phone: '', escort_relation: '',
    emergency_contact: '', notes: '',
    pickup_permissions: { regular: true, emergency: false, events: false },
  });
  const [escortPhoto, setEscortPhoto] = useState(null);
  const [escortPhotoPath, setEscortPhotoPath] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const [addSaving, setAddSaving] = useState(false);

  useEffect(() => {
    const init = async () => {
      const data = await InvokeApi(Config.main_url + apiList.getDeptartmentSection, { status: 'Active' });
      setClasses(data?.sections || []);
    };
    init();
  }, []);

  const classOptions = classes.map(c => ({
    value: `${c.standard?.trim()}||${c.section?.trim()}`,
    label: `${c.standard?.trim()} - ${c.section?.trim()}`,
  }));

  const handleClassChange = (val) => {
    setSelectedClass(val);
    setPage(1);
    if (val) {
      const [std, sec] = val.split('||');
      setStandard(std);
      setSection(sec);
    } else {
      setStandard('');
      setSection('');
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    const params = { page, limit: 20 };
    if (standard) params.standard = standard;
    if (section) params.section = section;

    const data = await InvokeApi(Config.main_url + apiList.getAllEscortPasses, params);

    if (data?.code === '200') {
      const cards = data.escort_cards || [];
      const map = {};
      cards.forEach(card => {
        const key = String(card.student_id);
        if (!map[key]) {
          map[key] = {
            student_id: card.student_id,
            student_name: card.student_name,
            standard: card.standard,
            section: card.section,
            roll_number: card.roll_number,
            escort_cards: [],
          };
        }
        map[key].escort_cards.push(card);
      });
      setStudents(Object.values(map));
      setPagination(data.pagination || {});
    } else {
      setStudents([]);
    }
    setLoading(false);
  }, [standard, section, search, page]);

  useEffect(() => { load(); }, [load]);

  const toggleExpand = (studentId) => {
    setExpanded(prev => {
      const s = new Set(prev);
      s.has(studentId) ? s.delete(studentId) : s.add(studentId);
      return s;
    });
  };

  const generatePass = async (card) => {
    setGenerating(p => ({ ...p, [card.id]: true }));
    const data = await InvokeApi(Config.main_url + apiList.generateEscortPass, {
      student_id: card.student_id,
      escort_card_id: card.id,
    });
    setGenerating(p => ({ ...p, [card.id]: false }));
    if (data?.code === '200') {
      load();
    } else {
      alert(data?.message || 'Failed to generate pass');
    }
  };

  const downloadPass = (filename) => {
    const url = `${Config.main_url}/downloadEscortPass/${filename}`;
    window.open(url, '_blank');
  };

  const handlePhotoUpload = async (file) => {
    setPhotoUploading(true);
    const fd = new FormData();
    fd.append('escort_photo', file);
    const res = await postImg(Config.main_url + apiList.addEscortPhoto, fd);
    if (res?.code === '200') setEscortPhotoPath(res.imageName || '');
    setPhotoUploading(false);
  };

  const handleAddCard = async () => {
    const studentId = addStudent?.student_id || addStudentId.trim();
    if (!studentId) { alert('Student ID is required'); return; }
    if (!addForm.escort_name || !addForm.escort_phone || !addForm.escort_relation) {
      alert('Name, Phone and Relation are required'); return;
    }
    if (!/^\d{10}$/.test(addForm.escort_phone)) {
      alert('Phone must be exactly 10 digits'); return;
    }
    setAddSaving(true);
    const data = await InvokeApi(Config.main_url + apiList.addEscortCard, {
      student_id: studentId,
      escort_name: addForm.escort_name,
      escort_phone: addForm.escort_phone,
      escort_relation: addForm.escort_relation,
      emergency_contact: addForm.emergency_contact,
      notes: addForm.notes,
      escort_photo: escortPhotoPath || '',
      pickup_permissions: addForm.pickup_permissions,
      valid_from: new Date().toISOString().split('T')[0],
      created_by: '1',
    });
    setAddSaving(false);
    if (data?.code === '200') {
      setAddDialog(false);
      setAddStudentId('');
      setAddForm({ escort_name: '', escort_phone: '', escort_relation: '', emergency_contact: '', notes: '', pickup_permissions: { regular: true, emergency: false, events: false } });
      setEscortPhoto(null); setEscortPhotoPath('');
      load();
    } else alert(data?.message || 'Failed');
  };

  const af = (k) => (e) => setAddForm(p => ({ ...p, [k]: e.target.value }));
  const togglePerm = (k) => setAddForm(p => ({ ...p, pickup_permissions: { ...p.pickup_permissions, [k]: !p.pickup_permissions[k] } }));

  const filteredStudents = search
    ? students.filter(s =>
      s.student_name?.toLowerCase().includes(search.toLowerCase()) ||
      String(s.roll_number || '').includes(search) ||
      s.escort_cards?.some(c =>
        c.escort_name?.toLowerCase().includes(search.toLowerCase()) ||
        String(c.escort_phone || '').includes(search)
      )
    )
    : students;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Escort Pass Manager</Typography>
          <Typography variant="body2" color="text.secondary">
            {filteredStudents.length} students · {filteredStudents.reduce((s, st) => s + (st.escort_cards?.length || 0), 0)} escort cards
          </Typography>
        </Box>
        <Box>
          <Tooltip title="Refresh">
            <IconButton onClick={load} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<Add />}
            onClick={() => {
              setAddStudent(null);
              setAddStudentId('');
              setAddForm({ escort_name: '', escort_phone: '', escort_relation: '', emergency_contact: '', notes: '', pickup_permissions: { regular: true, emergency: false, events: false } });
              setEscortPhoto(null); setEscortPhotoPath('');
              setAddDialog(true);
            }}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700, ml: 2 }}>
            Add Escort Card
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 3 }}>
        <CardContent sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField size="small" placeholder="Search student, escort name, phone..." value={search}
            onChange={e => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 240 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />

          <TextField select size="small" label="Class" value={selectedClass}
            onChange={e => handleClassChange(e.target.value)} sx={{ minWidth: 200 }}>
            <MenuItem value="">All Classes</MenuItem>
            {classOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
          <CircularProgress sx={{ color: '#1a237e' }} />
        </Box>
      ) : filteredStudents.length === 0 ? (
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CreditCard sx={{ fontSize: 64, color: '#e8eaf6', mb: 2 }} />
            <Typography color="text.secondary" fontWeight={500}>No escort cards found</Typography>
            <Typography variant="caption" color="text.secondary">
              {selectedClass ? `No cards for ${standard} - ${section}` : 'Select a class or search to filter'}
            </Typography>
          </Box>
        </Card>
      ) : (
        <Box>
          {filteredStudents.map((student, idx) => {
            const isExpanded = expanded.has(student.student_id);
            const totalCards = student.escort_cards?.length || 0;
            const passCount = student.escort_cards?.filter(c => c.has_pass).length || 0;

            return (
              <Card key={student.student_id || idx} sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 2 }}>
                <Box
                  onClick={() => toggleExpand(student.student_id)}
                  sx={{
                    p: 2, display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer',
                    borderBottom: isExpanded ? '1px solid #f0f0f0' : 'none',
                    '&:hover': { background: '#fafafa' },
                  }}
                >
                  <Avatar
                    src={student.profile_pic_path || student.profile_pic}
                    sx={{ width: 42, height: 42, background: 'linear-gradient(135deg,#1a237e,#1565c0)', fontWeight: 700 }}>
                    {student.student_name?.[0]}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography fontWeight={700} fontSize={14}>{student.student_name}</Typography>
                    <Typography fontSize={12} color="text.secondary">
                      {student.standard} - {student.section} · Roll: {student.roll_number || '—'}
                    </Typography>
                    <Typography fontSize={11} color="#1a237e" fontWeight={600}>
                      {totalCards} escort{totalCards !== 1 ? 's' : ''} · {passCount} pass{passCount !== 1 ? 'es' : ''} generated
                    </Typography>
                  </Box>
                  <Tooltip title="Add Escort Card">
                    <Button size="small" variant="outlined" startIcon={<Add />}
                      onClick={e => { e.stopPropagation(); setAddStudent(student); setAddDialog(true); }}
                      sx={{ borderRadius: 2, color: '#1a237e', borderColor: '#1a237e', fontWeight: 600, fontSize: 11, mr: 1 }}>
                      Add Card
                    </Button>
                  </Tooltip>
                  <IconButton size="small" sx={{ color: '#1a237e' }}>
                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>

                <Collapse in={isExpanded}>
                  <Box sx={{ p: 2 }}>
                    {student.escort_cards?.length === 0 ? (
                      <Typography color="text.secondary" fontSize={13} textAlign="center" py={2}>
                        No escort cards for this student
                      </Typography>
                    ) : (
                      student.escort_cards.map((card, ci) => {
                        const perms = card.pickup_permissions || {};
                        const permList = [
                          perms.regular && 'Regular',
                          perms.emergency && 'Emergency',
                          perms.events && 'Events',
                        ].filter(Boolean);
                        const isGen = generating[card.id];

                        return (
                          <Box key={card.id || ci} sx={{
                            mb: 1.5, p: 2, borderRadius: 2,
                            border: '1px solid #e8eaf6',
                            borderLeft: '4px solid #1a237e',
                            background: '#fafafa',
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                              {card.escort_photo ? (
                                <Box component="img" src={card.escort_photo} alt={card.escort_name}
                                  sx={{ width: 52, height: 52, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '2px solid #e8eaf6' }} />
                              ) : (
                                <Avatar sx={{ width: 52, height: 52, background: 'linear-gradient(135deg,#1a237e,#1565c0)', flexShrink: 0 }}>
                                  {card.escort_name?.[0]}
                                </Avatar>
                              )}

                              <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                  <Typography fontWeight={700} fontSize={14} color="#1a237e">{card.escort_name}</Typography>
                                  <Chip label={card.escort_relation} size="small" sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 600, fontSize: 10 }} />
                                  {card.has_pass ? (
                                    <Chip icon={<CheckCircle sx={{ fontSize: '14px !important' }} />} label="Pass Ready" size="small"
                                      sx={{ background: '#e8f5e9', color: '#2e7d32', fontWeight: 700, fontSize: 10 }} />
                                  ) : (
                                    <Chip icon={<Cancel sx={{ fontSize: '14px !important' }} />} label="No Pass" size="small"
                                      sx={{ background: '#ffebee', color: '#c62828', fontWeight: 700, fontSize: 10 }} />
                                  )}
                                </Box>
                                <Typography fontSize={12} color="text.secondary">📞 {card.escort_phone}</Typography>
                                {card.emergency_contact && (
                                  <Typography fontSize={11} color="text.disabled">Emergency: {card.emergency_contact}</Typography>
                                )}
                                {permList.length > 0 && (
                                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                    {permList.map(p => (
                                      <Chip key={p} label={p} size="small" sx={{ background: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: 10, height: 18 }} />
                                    ))}
                                  </Box>
                                )}
                                {card.latest_pass_generated && (
                                  <Typography fontSize={10} color="text.disabled" mt={0.3}>
                                    Last generated: {dayjs(card.latest_pass_generated).format('DD MMM YYYY')}
                                  </Typography>
                                )}
                              </Box>

                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, flexShrink: 0 }}>
                                <Button size="small" variant="contained" startIcon={isGen ? <CircularProgress size={12} color="inherit" /> : <CreditCard />}
                                  onClick={() => generatePass(card)} disabled={isGen}
                                  sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700, fontSize: 10, whiteSpace: 'nowrap' }}>
                                  {isGen ? 'Generating...' : card.has_pass ? 'Regenerate' : 'Generate Pass'}
                                </Button>
                                {card.has_pass && card.pass_files?.length > 0 && (
                                  <Button size="small" variant="outlined" startIcon={<Download />}
                                    onClick={() => downloadPass(card.pass_files[0].filename)}
                                    sx={{ borderRadius: 2, color: '#2e7d32', borderColor: '#2e7d32', fontWeight: 700, fontSize: 10 }}>
                                    Download
                                  </Button>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        );
                      })
                    )}
                  </Box>
                </Collapse>
              </Card>
            );
          })}

          {pagination.total_pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2, alignItems: 'center' }}>
              <Button variant="outlined" disabled={page === 1} onClick={() => setPage(p => p - 1)}
                sx={{ borderRadius: 2, color: '#1a237e', borderColor: '#1a237e' }}>Previous</Button>
              <Typography fontSize={13} color="text.secondary">
                Page {page} of {pagination.total_pages}
              </Typography>
              <Button variant="outlined" disabled={page === pagination.total_pages} onClick={() => setPage(p => p + 1)}
                sx={{ borderRadius: 2, color: '#1a237e', borderColor: '#1a237e' }}>Next</Button>
            </Box>
          )}
        </Box>
      )}

      <Dialog open={addDialog} onClose={() => setAddDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0' }}>
          {addStudent ? `Add Escort Card — ${addStudent.student_name}` : 'Add Escort Card'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            {!addStudent && (
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Student ID *" value={addStudentId}
                  onChange={e => setAddStudentId(e.target.value)}
                  placeholder="Enter student ID number"
                  helperText="Enter the student's ID to add an escort card for them" />
              </Grid>
            )}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {(escortPhoto || escortPhotoPath) ? (
                  <Box component="img"
                    src={escortPhoto ? URL.createObjectURL(escortPhoto) : escortPhotoPath}
                    sx={{ width: 60, height: 60, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e8eaf6' }} />
                ) : (
                  <Avatar sx={{ width: 60, height: 60, background: '#e8eaf6', color: '#1a237e', fontSize: 24 }}>👤</Avatar>
                )}
                <Button variant="outlined" component="label" sx={{ borderRadius: 2 }} disabled={photoUploading}>
                  {photoUploading ? 'Uploading...' : escortPhoto ? `✓ ${escortPhoto.name}` : 'Upload Photo'}
                  <input type="file" hidden accept="image/*" onChange={e => {
                    const file = e.target.files[0];
                    if (file) { setEscortPhoto(file); handlePhotoUpload(file); }
                  }} />
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Name *" value={addForm.escort_name} onChange={af('escort_name')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Phone * (10 digits)" value={addForm.escort_phone}
                onChange={e => setAddForm(p => ({ ...p, escort_phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                inputProps={{ maxLength: 10 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Relation *" value={addForm.escort_relation} onChange={af('escort_relation')}
                placeholder="e.g. Father, Mother, Uncle" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Emergency Contact" value={addForm.emergency_contact}
                onChange={e => setAddForm(p => ({ ...p, emergency_contact: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                inputProps={{ maxLength: 10 }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Notes" value={addForm.notes} onChange={af('notes')} multiline rows={2} />
            </Grid>
            <Grid item xs={12}>
              <Typography fontSize={13} fontWeight={600} color="#1a237e" mb={1}>Pickup Permissions</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {[{ key: 'regular', label: 'Regular' }, { key: 'emergency', label: 'Emergency' }, { key: 'events', label: 'Events' }].map(p => (
                  <Chip key={p.key} label={p.label} clickable onClick={() => togglePerm(p.key)}
                    sx={{
                      fontWeight: 700, fontSize: 12,
                      background: addForm.pickup_permissions[p.key] ? '#1a237e' : '#e8eaf6',
                      color: addForm.pickup_permissions[p.key] ? 'white' : '#1a237e',
                    }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setAddDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleAddCard} variant="contained" disabled={addSaving || photoUploading}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            {addSaving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
